'use client';

import { Prefix } from '@gnsx/genesys.js';
import * as ENGINE from '@gnsx/genesys.js';

import { buildClassInfo, getFilteredClassNames, registerGameClasses } from './classHelpers';

import type { SnapshotIn } from '@/editor/store/models';
import type { ClassInfo } from '@/editor/store/models';


const DEBUG_LOG = { logResourceClasses: false };

/**
 * Helper function to check if a class is marked as a resource (via decorator or inheritance)
 */
const isResourceSubclass = (constructor: any, ResourceClass: any): boolean => {
  // First check if it's marked as a resource via metadata
  if (ENGINE.ClassRegistry.isResourceClass(constructor)) {
    return true;
  }
  // Fall back to instanceof check for backwards compatibility
  return constructor !== ResourceClass && constructor.prototype instanceof ResourceClass;
};

/**
 * Gets class information for all registered Resource classes
 */
export async function getResourceClassInfos(shouldRegisterGameClasses = true): Promise<SnapshotIn<typeof ClassInfo>[]> {
  if (shouldRegisterGameClasses) {
    try {
      await registerGameClasses();
    } catch (error) {
      console.error('Error registering game classes', error);
    }
  }

  const { classRegistry, classNames, classPrefixesDisplay } = getFilteredClassNames();
  const resourceInfos: SnapshotIn<typeof ClassInfo>[] = [];

  // Get the Resource base class from ClassRegistry (Prefix.ENGINE already includes a trailing dot)
  const ResourceBaseClass = ENGINE.ClassRegistry.getClass(`${Prefix.ENGINE}Resource`);

  if (!ResourceBaseClass) {
    console.warn('❌ Resource base class not found in ClassRegistry. Make sure it is decorated with @EngineClass("Resource")');
    console.warn('Available classes:', classNames);
    return resourceInfos;
  }

  if (DEBUG_LOG.logResourceClasses) console.log('✅ Resource base class found:', ResourceBaseClass, '📋 All registered classes:', classNames);

  // Find all classes that extend Resource
  classNames.forEach(className => {
    const classConstructor = classRegistry.get(className);
    if (!classConstructor) return;

    // Debug: Check each class
    if (DEBUG_LOG.logResourceClasses && className.toLowerCase().includes('resource')) {
      console.log(`Checking class: ${className}`);
      console.log('  - Constructor:', classConstructor);
      console.log('  - Has prototype:', !!classConstructor.prototype);
      console.log('  - instanceof Resource:', classConstructor.prototype instanceof ResourceBaseClass);
      console.log('  - Is Resource itself:', classConstructor === ResourceBaseClass);
      console.log('  - isResourceSubclass result:', isResourceSubclass(classConstructor, ResourceBaseClass));
    }

    // Check if this class extends Resource (but is not Resource itself or another abstract base)
    if (isResourceSubclass(classConstructor, ResourceBaseClass) && !ENGINE.ClassRegistry.isAbstractClass(classConstructor)) {
      if (DEBUG_LOG.logResourceClasses) console.log('✅ Found Resource subclass:', className);
      resourceInfos.push(buildClassInfo(className, classPrefixesDisplay, 'Resource'));
    }
  });

  if (DEBUG_LOG.logResourceClasses) {
    console.log('Resource classes found:', resourceInfos);
  }

  return resourceInfos;
}

/**
 * Check if a class is a Resource subclass
 */
export function isResourceClass(className: string): boolean {
  const classConstructor = ENGINE.ClassRegistry.getClass(className);
  if (!classConstructor) return false;

  // First check if it's marked as a resource via metadata
  if (ENGINE.ClassRegistry.isResourceClass(classConstructor)) {
    return true;
  }

  // Fall back to instanceof check for backwards compatibility
  const ResourceBaseClass = ENGINE.ClassRegistry.getClass(`${Prefix.ENGINE}Resource`);
  if (!ResourceBaseClass) return false;

  return isResourceSubclass(classConstructor, ResourceBaseClass);
}

